import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { showToast } from 'src/utils/toast'
import { DateTime } from 'luxon'
import { useSelector } from 'react-redux'
import { getContractorPortalResendAvailableAt, getResendAvailableAt } from 'src/selectors/auth'
import { SignInPhoneResponse } from 'src/interfaces/auth'

interface Props {
  disabled?: boolean
  resendLinkTestId?: string
  platform: 'client' | 'contractorPortal'
  onResend(): Promise<SignInPhoneResponse | undefined>
}

const ResendCodeComponent = ({ disabled, onResend, resendLinkTestId, platform }: Props) => {
  const resendInterval = useRef<NodeJS.Timer | null>(null)

  const clientResendAvailableAt = useSelector(getResendAvailableAt)
  const contractorPortalResendAvailableAt = useSelector(getContractorPortalResendAvailableAt)

  const [resendTime, setResendTime] = useState<number>(0)
  const [isLoading, setIsLoading] = useState(false)

  const resendAvailableAt =
    platform === 'contractorPortal' ? contractorPortalResendAvailableAt : clientResendAvailableAt

  useEffect(() => {
    if (!resendAvailableAt) {
      setResendTime(0)

      return
    }

    const updateRemaining = () => {
      const remainingMs = Math.max(
        0,
        DateTime.fromISO(resendAvailableAt, { zone: 'utc' }).diffNow().toMillis(),
      )

      setResendTime(remainingMs)

      return remainingMs
    }

    if (updateRemaining() <= 0) {
      return
    }

    resendInterval.current = setInterval(() => {
      if (updateRemaining() <= 0 && resendInterval.current) {
        clearInterval(resendInterval.current)

        resendInterval.current = null
      }
    }, 1000)

    return () => {
      if (resendInterval.current) {
        clearInterval(resendInterval.current)

        resendInterval.current = null
      }
    }
  }, [resendAvailableAt])

  const handleResend = useCallback(async () => {
    setIsLoading(true)

    try {
      if (disabled || resendTime) {
        return
      }

      const response = await onResend()

      if (!response) {
        return
      }

      const { resendRemainingCount, smsSend, codeSent } = response

      const isCodeSent = smsSend || codeSent

      if (resendRemainingCount === 1) {
        showToast(
          'You have 1 code request remaining before code requests are temporarily disabled.',
          { variant: 'warning' },
        )
      }

      if (resendRemainingCount === 0) {
        if (isCodeSent) {
          showToast("You've reached the code request limit. Try again in 30 minutes.", {
            variant: 'error',
          })
        } else {
          showToast("You've reached the code request limit. Try again later.", { variant: 'error' })

          return
        }
      }

      if (isCodeSent) {
        showToast('A new verification code has been sent.', { variant: 'success' })
      } else {
        showToast(
          'A verification code has already been sent. Please wait before requesting a new one.',
          { variant: 'warning' },
        )
      }
    } catch (err) {
      //
    }

    setIsLoading(false)
  }, [disabled, resendTime, onResend])

  return (
    <Typography css={tw`flex flex-row justify-center text-shades-400`}>
      Didn't receive it? &nbsp;
      <Typography
        css={[
          resendTime ? undefined : tw`cursor-pointer`,
          resendTime ? tw`text-shades-1000` : tw`text-primary-500`,
        ]}
        onClick={resendTime || isLoading ? undefined : handleResend}
        variant="h4"
        {...(resendLinkTestId ? { 'data-testid': resendLinkTestId } : {})}
      >
        {resendTime
          ? `Resend new in ${DateTime.fromMillis(Math.ceil(resendTime / 1000) * 1000).toFormat(
              'mm:ss',
            )}`
          : 'Tap to resend'}
      </Typography>
    </Typography>
  )
}

export const ResendCode = memo(ResendCodeComponent)
